import React, { useContext, useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom';
import Header from './components/Header/Header';
import Home from './components/Home/Home';
import About from './components/About/About';
import Archive from './components/Archive/Archive';
import Story from './components/Story/Story';
import AdminDashboard from './components/Admin/AdminDashboard';
import Signup from './components/Account/Signup/Signup';
import Interests from './components/Account/Interests/Interests';
import Profile from './components/Account/Profile/Profile';
import Login from './components/Account/Login/Login';
import StoryRaw from './components/StoryRaw/StoryRaw';
import StoriesRaw from './components/StoriesRaw/StoriesRaw';
import { UserContext } from './hooks/UserContext';
import { HostInfoContext } from './hooks/HostInfoContext';
import useFindUser from './hooks/useFindUser';
import useAuth from './hooks/useAuth';
import { GoogleOAuthProvider } from '@react-oauth/google';
import axios from 'axios';
import { setBaseUrlFromDDDomain } from './components/Stories/Links';
import './index.css';

const PrivateRoute = ({ isLoading, children, admin = false }) => {
  const { user } = useContext(UserContext);
  const { check_admin } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);
  let location = useLocation();

  useEffect(() => {
    const fetchAdminStatus = async () => {
      const res = await check_admin();
      setIsAdmin(res);
      setAdminLoading(false); // admin check finished
    };

    if (admin && user) {
      fetchAdminStatus();
    } else {
      setAdminLoading(false); // No admin check needed
    }
  }, [admin, check_admin, user]);

  if (isLoading || adminLoading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" state={{ from: location }} />;
  }

  if (admin && !isAdmin) {
    return <div>Not authorized -- administrator permissions required.</div>; // Show not authorized for admin routes
  }

  if (
    (!user.name || !user.news_organization) &&
    location.pathname !== '/profile'
  ) {
    // Redirect to profile if name or news_organization is empty and not already on profile page
    return <Navigate to="/profile" state={{ from: location }} />;
  }

  return children; // If all checks pass, render children (protected content)
};

// Route is used to navigate to the separate pages such as Homepage, About, Archive, etc.
function App() {
  const { user, setUser, isLoading } = useFindUser();
  const [hostInfo, setHostInfo] = useState(null);

  const FEATURED_API = `${process.env.REACT_APP_FEATURED_API}`;

  useEffect(() => {
    // Fetch hostInfo when user is available (for authentication)
    if (user && localStorage.getItem('token')) {
      const axiosInstance = axios.create({
        baseURL: FEATURED_API,
        headers: {
          Authorization: `JWT ${localStorage.getItem('token')}`,
        },
      });

      axiosInstance
        .get('/host_info/')
        .then((res) => {
          setHostInfo(res.data);
          // Update base_url with DD_DOMAIN
          if (res.data?.DD_DOMAIN) {
            setBaseUrlFromDDDomain(res.data.DD_DOMAIN);
          }
        })
        .catch((err) => {
          console.log('Error fetching host info:', err);
        });
    }
  }, [user, FEATURED_API]);

  return (
    <GoogleOAuthProvider clientId="891898669780-734dqak2rq00s4ilock73mc60le86dlv.apps.googleusercontent.com">
      <Router>
        <UserContext.Provider value={{ user, setUser }}>
          <HostInfoContext.Provider value={hostInfo}>
            <Header />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <PrivateRoute isLoading={isLoading}>
                  <Home />
                </PrivateRoute>
              }
            />
            <Route
              path="/about"
              element={
                <PrivateRoute isLoading={isLoading}>
                  <About />
                </PrivateRoute>
              }
            />
            <Route
              path="/archive"
              element={
                <PrivateRoute isLoading={isLoading}>
                  <Archive />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute isLoading={isLoading} admin={true}>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/signup"
              element={
                <PrivateRoute isLoading={isLoading} admin={true}>
                  <Signup />
                </PrivateRoute>
              }
            />
            <Route
              path="/interests"
              element={
                <PrivateRoute isLoading={isLoading}>
                  <Interests />
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute isLoading={isLoading}>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/util/:storyid"
              element={
                <PrivateRoute isLoading={isLoading}>
                  <StoryRaw />
                </PrivateRoute>
              }
            />
            <Route
              path="/util"
              element={
                <PrivateRoute isLoading={isLoading}>
                  <StoriesRaw />
                </PrivateRoute>
              }
            />
            <Route
              path="/:storyid"
              element={
                <PrivateRoute isLoading={isLoading}>
                  <Story />
                </PrivateRoute>
              }
            />
          </Routes>
          </HostInfoContext.Provider>
        </UserContext.Provider>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
